import { useState } from "react";
import { ModifierGroup, Product } from "../types/pos.types";

export interface CartItem {
  item_id?: string;
  id: string;
  product_id: string;
  name: string;
  basePrice: number;
  price: number;
  quantity: number;
  modifiers: Record<string, string | string[]>;
  modifierDetails: Array<{
    id: string;
    name: string;
    price: number;
  }>;
}

export interface OrderDiscount {
  type: "PERCENTAGE" | "FIXED";
  value: number;
  reason?: string;
  appliedBy?: string;
  timestamp?: number;
}

export const useCart = () => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [appliedDiscount, setAppliedDiscount] = useState<OrderDiscount | null>(
    null
  );

  const addItem = (
    product: Product,
    modifiers: Record<string, string | string[]> = {}
  ) => {
    const modifierKey = JSON.stringify(modifiers);
    const uniqueId = `${product._id}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 5)}`;

    // ✅ Use real modifiers from the product instead of dummy ones
    const modifierDetails = Object.entries(modifiers).flatMap(
      ([groupId, selections]) => {
        console.log("Processing group:", groupId);
        console.log("Raw selections:", selections);
        console.log("Product modifiers:", product.modifiers);

        const group = product.modifiers?.find(
          (g: { id: string }) => g.id === groupId
        );

        if (!group) {
          console.warn(`Modifier group "${groupId}" not found in product`);
          return [];
        }

        console.log(`Found group:`, group);

        if (group.type === "radio" && typeof selections === "string") {
          console.log("Radio group detected. Selection ID:", selections);

          const mod = group.modifiers.find(
            (m: { id: string }) => m.id === selections
          );

          if (mod) {
            console.log("Matched modifier:", mod);
            return [{ ...mod }];
          } else {
            console.warn(
              "No matching modifier found for selection:",
              selections
            );
            return [];
          }
        } else if (group.type === "checkbox" && Array.isArray(selections)) {
          console.log("Checkbox group detected. Selected IDs:", selections);

          const matchedModifiers = group.modifiers.filter((m: { id: string }) =>
            selections.includes(m.id)
          );

          console.log("Matched modifiers:", matchedModifiers);
          return matchedModifiers;
        } else {
          console.warn(`Unexpected type or format in group "${groupId}"`);
          return [];
        }
      }
    );

    const modifierSum = modifierDetails.reduce((sum, m) => sum + m.price, 0);
    const totalPrice = (product.basePrice || product.price) + modifierSum;

    setItems((prevItems) => [
      ...prevItems,
      {
        id: uniqueId,
        product_id: product._id || product.item_id,
        name: product.name,
        basePrice: product.basePrice || product.price,
        price: totalPrice,
        quantity: 1,
        modifiers,
        modifierDetails,
      },
    ]);
  };

  const removeItem = (productId: string) => {
    setItems((prevItems) =>
      prevItems.filter((item) => item.product_id !== productId)
    );
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const handleEditCartItem = (item: CartItem) => {
    console.log("Edit item:", item);
  };

  const handleDeleteCartItem = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateModifiers = (
    itemId: string,
    modifiers: Record<string, string | string[]>
  ) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, modifiers } : item
      )
    );
  };

  const updatePrice = (itemId: string, newPrice: number) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, price: newPrice } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const tax = subtotal * 0.1; // 5% tax

  let finalTotal = subtotal + tax;

  if (appliedDiscount && appliedDiscount.value > 0) {
    if (appliedDiscount.type === "PERCENTAGE") {
      const discountAmount = finalTotal * (appliedDiscount.value / 100);
      finalTotal -= discountAmount;
    } else {
      finalTotal -= appliedDiscount.value;
    }
    finalTotal = Math.max(finalTotal, 0);
  }

  const applyDiscount = (discount: OrderDiscount) => {
    setAppliedDiscount(discount);
  };

  const clearDiscount = () => {
    setAppliedDiscount(null);
  };

  return {
    items,
    addItem,
    removeItem,
    updateQuantity,
    updateModifiers,
    updatePrice,
    clearCart,
    handleEditCartItem,
    handleDeleteCartItem,
    setAppliedDiscount,
    subtotal,
    tax,
    total: finalTotal,
    appliedDiscount,
    applyDiscount,
    clearDiscount,
  };
};
